import React, { useEffect, useMemo, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CarrierAppointmentsByStateMap from 'tailwind-components/CarrierAppointments/CarrierAppointmentsByStateMap';
import Button from 'tailwind-components/Button/Button';
import {
  NO_CARRIERS_MATCH_FILTERS_MESSAGE,
  getCarrierCategoryLabel,
  getCarrierPartnersMapFromCarriers,
  getCarrierStateDisplayStatus,
  getFullStateMapForDisplay
} from 'helpers/carriers/carriers';
import { getStateNameFromStateCode } from 'helpers/states';
import { usePortalWalkthrough } from 'tailwind-components/PortalWalkthrough/PortalWalkthroughContext';
import useCarrierTypeFilter from 'hooks/useCarrierTypeFilter';

const PENDING_AGENT_ACTION_STYLES =
  'bg-semantic-danger/40 text-gray-900 border border-semantic-danger data-[selected=true]:ring-2 data-[selected=true]:ring-semantic-danger';

const STATUS_STYLES = {
  confirmed:
    'bg-secondary-light text-gray-900 border border-secondary data-[selected=true]:ring-2 data-[selected=true]:ring-secondary',
  pending:
    'bg-pending/50 text-gray-900 border border-pending data-[selected=true]:ring-2 data-[selected=true]:ring-pending',
  'pending-agent-action': PENDING_AGENT_ACTION_STYLES,
  'not-appointed':
    'bg-grey-lightest text-gray-600 border border-grey-lighter data-[selected=true]:ring-2 data-[selected=true]:ring-grey-light',
  'offered-not-available':
    'bg-slate-100 text-gray-800 border border-slate-300 data-[selected=true]:ring-2 data-[selected=true]:ring-slate-400'
};

const LEGEND = [
  { id: 'confirmed', label: 'Confirmed', dot: 'bg-secondary' },
  { id: 'pending', label: 'Pending', dot: 'bg-pending' },
  {
    id: 'pending-agent-action',
    label: 'Pending Agent Action',
    dot: 'bg-semantic-danger'
  },
  {
    id: 'offered-not-available',
    label: 'Offered (Not available)',
    dot: 'bg-slate-300'
  },
  { id: 'not-appointed', label: 'Not Appointed', dot: 'bg-grey-lighter' }
];

function sortNames(list) {
  return [...list].sort((a, b) => a.localeCompare(b));
}

const MOCK_STATUSES = ['confirmed', 'pending', 'pending-agent-action', 'not-appointed', 'not-appointed'];

export default function CarrierResourcesByState({
  selectedCarrier,
  onCarrierClick
}) {
  const navigate = useNavigate();
  const tourCtx = usePortalWalkthrough();
  const isTourActive = tourCtx?.isActive ?? false;

  const user = useSelector(state => state.user.zohoData);
  const { carriersArray, requestedAppointments, actionableAppointments } =
    useSelector(state => state.appointments);

  const [selectedStateCode, setSelectedStateCode] = useState(null);
  const getAppointedRef = useRef(null);

  useEffect(() => {
    if (selectedStateCode) {
      setTimeout(() => {
        let el = getAppointedRef.current?.parentElement;
        while (el) {
          const { overflowY } = window.getComputedStyle(el);
          if (overflowY === 'auto' || overflowY === 'scroll') {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            break;
          }
          el = el.parentElement;
        }
      }, 300);
    }
  }, [selectedStateCode]);

  const carrierTypeFilter = useCarrierTypeFilter(carriersArray);

  const carrierPartnersByState = useMemo(
    () =>
      getCarrierPartnersMapFromCarriers(
        carrierTypeFilter.filteredCarriers,
        user,
        requestedAppointments
      ),
    [carrierTypeFilter.filteredCarriers, user, requestedAppointments]
  );

  const carrierPartnersMap = useMemo(
    () => getFullStateMapForDisplay(carrierPartnersByState),
    [carrierPartnersByState]
  );

  const stateCarrierNames = useMemo(
    () =>
      selectedStateCode == null
        ? []
        : carrierPartnersByState[selectedStateCode]?.carriers ?? [],
    [carrierPartnersByState, selectedStateCode]
  );

  const mockCarriersWithStatus = useMemo(() => {
    if (!isTourActive || !selectedStateCode) return [];
    return carriersArray.slice(0, 5).map((c, i) => ({
      name: c.name,
      status: MOCK_STATUSES[i] ?? 'not-appointed',
      category: getCarrierCategoryLabel(c)
    }));
  }, [isTourActive, selectedStateCode, carriersArray]);

  const carriersWithStatus = useMemo(() => {
    if (isTourActive) return mockCarriersWithStatus;
    if (!stateCarrierNames.length || !selectedStateCode) return [];
    const stateName = getStateNameFromStateCode(selectedStateCode);
    return sortNames(stateCarrierNames).map(name => {
      const carrierData = carriersArray.find(c => c.name === name);
      return {
        name,
        category: getCarrierCategoryLabel(carrierData),
        status: getCarrierStateDisplayStatus(
          carrierData,
          stateName,
          user,
          requestedAppointments,
          actionableAppointments
        )
      };
    });
  }, [
    isTourActive,
    mockCarriersWithStatus,
    carriersArray,
    stateCarrierNames,
    selectedStateCode,
    user,
    actionableAppointments,
    requestedAppointments
  ]);

  const selectedCarrierData = useMemo(
    () =>
      selectedCarrier
        ? carriersArray.find(c => c.name === selectedCarrier)
        : null,
    [selectedCarrier, carriersArray]
  );

  const appointmentsDisabled =
    selectedCarrierData?.appointmentsDisabled ?? true;

  function handleStateClick(code) {
    if (!carrierPartnersMap[code] && !isTourActive) return;
    setSelectedStateCode(code);
    onCarrierClick('');
  }

  const stateName = selectedStateCode
    ? getStateNameFromStateCode(selectedStateCode)
    : null;

  return (
    <div data-tour='carrier-resources-map' className='flex flex-col gap-4'>
      <button
        data-tour='carrier-resources-state-deselect'
        style={{ display: 'none' }}
        onClick={() => setSelectedStateCode(null)}
      />
      {/* Map — fixed height so content below stays visible */}
      <div className='rounded-xl'>
        <CarrierAppointmentsByStateMap
          carrierPartnersMap={carrierPartnersMap}
          onStateClick={handleStateClick}
          selectedStateCode={selectedStateCode}
          carrierTypeOptions={carrierTypeFilter.options}
          selectedCarrierTypes={carrierTypeFilter.selectedTypes}
          onCarrierTypesChange={carrierTypeFilter.setSelectedTypes}
        />
      </div>

      {/* Carriers section — only when a state is selected */}
      {selectedStateCode && (
        <>
          <hr className='border-dashed border-gray-200 m-0' />

          <section>
            <div className='flex justify-between items-center mb-3 lg:grid lg:grid-cols-[1fr_auto_1fr]'>
              <h3 className='text-3xl font-bold text-gray-700 m-0 lg:col-start-2 lg:text-center'>
                {stateName}
              </h3>
              <div className='flex flex-col items-end lg:mr-10'>
                <div className='flex flex-col gap-0.5 text-xs lg:text-sm text-gray-500'>
                  {LEGEND.map(l => (
                    <span key={l.id} className='flex items-center gap-1.5'>
                      <span
                        className={`w-2.5 h-2.5 rounded-full inline-block shrink-0 ${l.dot}`}
                      />
                      {l.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {carriersWithStatus.length === 0 ? (
              <p className='text-sm text-gray-400'>
                {carrierTypeFilter.selectedTypes.length > 0
                  ? NO_CARRIERS_MATCH_FILTERS_MESSAGE
                  : 'No carriers available for this state.'}
              </p>
            ) : (
              <div
                data-tour='carrier-resources-by-state-carriers'
                className='grid grid-cols-4 lg:grid-cols-5 gap-x-3 gap-y-1.5'
              >
                {carriersWithStatus.map(({ name, status, category }) => (
                  <button
                    key={name}
                    type='button'
                    data-selected={selectedCarrier === name}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold select-none cursor-pointer transition-all duration-100 ring-offset-1 ${STATUS_STYLES[status]}`}
                    onClick={() => onCarrierClick(name)}
                  >
                    {name}
                    {category && (
                      <span className='mt-0.5 block text-xs font-normal opacity-70'>
                        {category}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </section>

          <div ref={getAppointedRef} className='flex justify-center'>
            <Button
              variant={
                !selectedCarrier || appointmentsDisabled ? 'default' : 'success'
              }
              disabled={!selectedCarrier || appointmentsDisabled}
              text='Get Appointed'
              size='medium'
              onClick={() =>
                navigate(`/carrier-appointments?carrier=${selectedCarrier}`)
              }
            />
          </div>
        </>
      )}
    </div>
  );
}

CarrierResourcesByState.propTypes = {
  selectedCarrier: propTypes.string,
  onCarrierClick: propTypes.func.isRequired
};
